<script setup lang="ts">
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { RouteLocationRaw, useRoute } from 'vue-router'
import { ref } from 'vue'

import { useAuthStore } from '@/stores/auth-store'
import { PaginatedResponse } from '@/types/general'
import { MinimalLoadingListRoute } from '@/types/cargo-loading'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { GetDataParameters } from '@/types/table'
import { datetime } from '@/utils/dates'
import { nameAndShortCodeString } from '@/utils/cargo-loading'

import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'
import MyFilterButton from '@/components/table/MyFilterButton.vue'
import MyFilterDivider from '@/components/table/MyFilterDivider.vue'
import RouteSplitModal from '@/components/cargo-loading/RouteSplitModal.vue'

interface FetchLoadingListRoutesParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const authStore = useAuthStore()
const route = useRoute()
const { t } = useI18n()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalLoadingListRoute,
    FetchLoadingListRoutesParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<MinimalLoadingListRoute>>(
            window.route('cl.company.loading-list-routes.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            'start-date': dayjs().startOf('day').toDate().toISOString(),
            'end-date': dayjs().add(2, 'days').endOf('day').toDate().toISOString(),
        },
    },
)

const splitModalOpen = ref(false)
const routeIdToSplit = ref<string | null>(null)

function canSplitRoute(row: MinimalLoadingListRoute): boolean {
    return row.itemsCount > 1
}

function showLoadingList(id: string): RouteLocationRaw {
    return {
        name: 'cl.loading-lists.show',
        params: { listId: id },
    }
}

function setFilter(value: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = value
    params.value.page = 1
}

function closeSplitModal() {
    splitModalOpen.value = false
    routeIdToSplit.value = null
}

function openSplitModal(routeId: string) {
    routeIdToSplit.value = routeId
    splitModalOpen.value = true
}
</script>

<template>
    <MyTable
        table-id="routes"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('routes')"
    >
        <template #actionRow="{ row }">
            <RouterLink
                :to="{ name: 'cl.routes.show', params: { routeId: row.id }, query: route.query }"
            >
                <ActionRowItem>
                    <mdi:eye />
                </ActionRowItem>
            </RouterLink>
            <ActionRowItem
                v-if="canSplitRoute(row)"
                v-tooltip="t('splitRoute')"
                @click="openSplitModal(row.id)"
            >
                <mdi:call-split />
            </ActionRowItem>
        </template>

        <template #filters>
            <MyDateRangePicker
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />

            <MyFilterDivider />

            <MyFilterButton
                filter-name="state"
                value="unlinkedRoutes"
                default
                @selected="setFilter"
                v-text="t('unlinkedRoutes')"
            />
            <MyFilterButton
                filter-name="state"
                value="linkedRoutes"
                @selected="setFilter"
                v-text="t('linkedRoutes')"
            />
            <MyFilterButton
                filter-name="state"
                value="all"
                @selected="setFilter"
                v-text="t('all')"
            />
        </template>
        <MyTableColumn
            :name="t('carrier')"
            property="carrier.name"
            :tooltip="t('carrierExplanation')"
        />
        <template #[`carrier.name`]="{ row }">
            {{ nameAndShortCodeString(row.carrier.name, row.carrierAccountNumber) }}
        </template>
        <MyTableColumn
            :name="t('carrierNumber')"
            property="carrierNumber"
            :tooltip="t('carrierNumberExplanation')"
        />
        <MyTableColumn
            :name="t('departedAt')"
            property="departedAt"
            :tooltip="t('departedAtExplanation')"
        />
        <template #departedAt="{ row }">
            {{ datetime(row.departedAt) }}
        </template>
        <MyTableColumn :name="t('remark')" property="remark" :tooltip="t('remarkExplanation')" />
        <MyTableColumn :name="t('order')" property="order" :tooltip="t('orderExplanation')" />
        <MyTableColumn :name="t('items')" property="itemsCount" :tooltip="t('itemsExplanation')" />
        <MyTableColumn :name="t('destination')" property="destinationPort.shortName" />
        <MyTableColumn
            :name="t('linkedList')"
            property="loadingList"
            :tooltip="t('linkedList')"
            :sortable="false"
        />
        <template #loadingList="{ row }">
            <RouterLink
                v-if="row.loadingList"
                v-tooltip="t('goTo', { to: t('loadingList').toLowerCase() })"
                :to="showLoadingList(row.loadingList.id)"
            >
                {{ row.loadingList.tripNumber }}
            </RouterLink>
            <span v-else>{{ t('unlinkedRoute') }}</span>
        </template>
    </MyTable>

    <RouteSplitModal
        v-if="routeIdToSplit"
        :model-value="splitModalOpen"
        :route-id="routeIdToSplit"
        @close="closeSplitModal"
        @saved="refetch"
    />
</template>
